import { ChevronDown } from 'lucide-react';

import { useNavigationStore } from '@/stores/navigation';

import { FeatureGroup } from './FeatureGroup';

import type { Platform } from '@/types/navigation';
import type { ReactNode } from 'react';

type Props = { platform: Platform; collapsed?: boolean };
const labelStyle = { fontFamily: 'var(--font-mono, monospace)' };
const btnCls =
  'w-full flex items-center gap-2 px-3 py-2 rounded-md hover:bg-[#1A1A1A] overflow-hidden';
const labelCls =
  'flex-1 text-left text-xs font-semibold uppercase tracking-wide whitespace-nowrap';

function CollapsedIcon({ icon: Icon }: { icon: Platform['icon'] }): ReactNode {
  return (
    <div
      style={{ color: 'var(--text-muted)' }}
      className="flex justify-center py-3"
    >
      <Icon className="w-5 h-5" />
    </div>
  );
}

function ExpandedContent({ platform }: { platform: Platform }): ReactNode {
  return (
    <div className="mt-1 ml-2 border-l border-[#1A1A1A] pl-2">
      {platform.features.map((f) => (
        <FeatureGroup key={f.id} feature={f} />
      ))}
    </div>
  );
}

export function PlatformGroup({ platform, collapsed }: Props): ReactNode {
  const { expanded, toggle } = useNavigationStore();
  if (collapsed) {
    return <CollapsedIcon icon={platform.icon} />;
  }

  const isExpanded = expanded.has(platform.id);
  const Icon = platform.icon;
  const chevronCls = `w-4 h-4 shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`;

  return (
    <div className="py-2">
      <button
        onClick={() => toggle(platform.id)}
        style={{ color: 'var(--text-primary)' }}
        className={btnCls}
      >
        <Icon className="w-4 h-4 shrink-0" />
        <span style={labelStyle} className={labelCls}>
          {platform.label}
        </span>
        <ChevronDown className={chevronCls} />
      </button>
      {isExpanded && <ExpandedContent platform={platform} />}
    </div>
  );
}
